import { orderHasItemsForStation } from '@shared/lib/orderStationProcessing'
import {
  mergeOrderModeSettings,
  shouldChimeForEvent,
  type OrderModeSettings,
} from '@shared/lib/orderModeSettings'
import type { FoodItem, Order, OrderStatus } from '../types'

export type OrderChimeSource = 'this_device' | 'customer_app' | 'other_pos' | 'delivery'

export interface ChimeSourcePrefs {
  chimeOnPendingFromThisDevice: boolean
  chimeOnPendingFromCustomerApp: boolean
  chimeOnPendingFromOtherPos: boolean
  chimeOnPendingFromDelivery: boolean
}

export { DEFAULT_CHIME_SOURCE_PREFS } from '@shared/lib/placeDefaults'

const DELIVERY_SOURCES = new Set(['grab', 'gofood', 'application'])

export interface ChimePolicyContext {
  localDeviceId: string
  stationFilter: string
  menu: readonly FoodItem[]
  prefs: ChimeSourcePrefs
  orderModeSettings?: OrderModeSettings
}

export function isProcessingColumnStatus(status: OrderStatus): boolean {
  return status === 'processing' || status === 'submitted'
}

export function classifyOrderChimeSource(
  order: Order,
  localDeviceId: string,
): OrderChimeSource | null {
  const source = order.orderSource?.trim() ?? ''
  if (source === 'customer_app') return 'customer_app'
  if (DELIVERY_SOURCES.has(source)) return 'delivery'
  if (source === 'pos') {
    const placedBy = order.placedByPosDeviceId?.trim()
    if (placedBy && placedBy === localDeviceId) return 'this_device'
    return 'other_pos'
  }
  return null
}

export function isChimeSourceEnabled(
  source: OrderChimeSource,
  prefs: ChimeSourcePrefs,
): boolean {
  switch (source) {
    case 'this_device':
      return prefs.chimeOnPendingFromThisDevice
    case 'customer_app':
      return prefs.chimeOnPendingFromCustomerApp
    case 'other_pos':
      return prefs.chimeOnPendingFromOtherPos
    case 'delivery':
      return prefs.chimeOnPendingFromDelivery
  }
}

export function orderPassesSourceFilter(order: Order, ctx: ChimePolicyContext): boolean {
  const source = classifyOrderChimeSource(order, ctx.localDeviceId)
  if (!source) return false
  return isChimeSourceEnabled(source, ctx.prefs)
}

function orderMatchesStationFilter(order: Order, ctx: ChimePolicyContext): boolean {
  const station = ctx.stationFilter.trim()
  if (!station) return true
  return orderHasItemsForStation(order, ctx.menu, station)
}

/** Orders that should appear in the banner and count toward alert state. */
export function getAlertableOrders(orders: Order[], ctx: ChimePolicyContext): Order[] {
  const stationActive = ctx.stationFilter.trim().length > 0
  const orderModeSettings = mergeOrderModeSettings(ctx.orderModeSettings)
  const showOrderReceived = shouldChimeForEvent('order_received', orderModeSettings)
  const showPaymentReceived = shouldChimeForEvent('payment_received', orderModeSettings)
  return orders.filter((order) => {
    if (!orderPassesSourceFilter(order, ctx)) return false
    if (!orderMatchesStationFilter(order, ctx)) return false
    if (stationActive) {
      return isProcessingColumnStatus(order.status)
    }
    return (
      (showOrderReceived && order.status === 'pending') ||
      (showPaymentReceived && order.status === 'processing')
    )
  })
}

export function getAlertMode(stationFilter: string): 'pending' | 'station' {
  return stationFilter.trim().length > 0 ? 'station' : 'pending'
}

/** Returns orders that should trigger a chime on this update tick. */
export function detectChimeEvents(
  prevSnapshot: ReadonlyMap<string, OrderStatus>,
  orders: Order[],
  ctx: ChimePolicyContext,
  snapshotReady: boolean,
): Order[] {
  if (!snapshotReady) return []

  const stationActive = ctx.stationFilter.trim().length > 0
  const orderModeSettings = mergeOrderModeSettings(ctx.orderModeSettings)
  const chimeOrderReceived = shouldChimeForEvent('order_received', orderModeSettings)
  const chimePaymentReceived = shouldChimeForEvent('payment_received', orderModeSettings)
  const orderById = new Map(orders.map((o) => [o.id, o]))
  const chimeOrders: Order[] = []

  if (stationActive || chimePaymentReceived) {
    for (const order of orders) {
      if (order.status !== 'processing') continue
      const prevStatus = prevSnapshot.get(order.id)
      if (prevStatus == null) continue
      if (prevStatus === 'processing') continue
      if (!orderPassesSourceFilter(order, ctx)) continue
      if (!orderMatchesStationFilter(order, ctx)) continue
      chimeOrders.push(order)
    }
  }

  if (stationActive || !chimeOrderReceived) return chimeOrders

  if (chimeOrderReceived) {
    for (const order of orders) {
      if (order.status !== 'pending') continue
      if (prevSnapshot.has(order.id)) continue
      if (order.splitSilent) continue
      if (!orderPassesSourceFilter(order, ctx)) continue
      chimeOrders.push(order)
    }
  }

  // Also handle orders that were not in prev snapshot but are pending (edge: prev was empty id)
  for (const [id, status] of prevSnapshot) {
    if (!orderById.has(id) && status === 'pending') {
      // removed from list — no chime
    }
  }

  return chimeOrders
}

export function buildOrdersStatusSnapshot(orders: Order[]): Map<string, OrderStatus> {
  return new Map(orders.map((o) => [o.id, o.status]))
}

export function shouldShowAlertBanner(
  alertableOrders: Order[],
  dismissedIds: ReadonlySet<string>,
): boolean {
  if (alertableOrders.length === 0) return false
  return alertableOrders.some((o) => !dismissedIds.has(o.id))
}
